/**
 * web/src/hooks/mutations/useSyncTemplates.ts
 * Admin-only re-ingest of an event's ops template Docs into stored, pickable
 * sections. On success invalidates the templates query so the status view
 * refreshes, and toasts a per-doc summary (surfacing any errored doc).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { syncEventTemplates, type TemplateSyncResult } from '../../api/events';
import { toastApiError } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * Syncs an event's ops template Docs. Bound to a single event id.
 *
 * @param eventId the event whose Docs to re-ingest
 * @returns React Query mutation handle (TemplateSyncResult[])
 * @sideEffects HTTP POST; Doc fetch + Anthropic calls; upserts TemplateSource rows
 * @idempotent yes — re-syncing the same Docs yields the same sections
 */
export function useSyncTemplates(eventId: string) {
  const qc = useQueryClient();
  return useMutation<TemplateSyncResult[], unknown, void>({
    mutationFn: () => syncEventTemplates(eventId),
    onSuccess: (results) => {
      const errored = results.filter((r) => r.status === 'error');
      const totalSections = results.reduce((n, r) => n + r.sectionCount, 0);
      if (errored.length > 0) {
        toast.error(
          `Synced with ${errored.length} error${errored.length === 1 ? '' : 's'} — see the status below`,
        );
      } else {
        toast.success(
          `Templates synced — ${totalSections} section${totalSections === 1 ? '' : 's'} across ${results.length} doc${results.length === 1 ? '' : 's'}`,
        );
      }
      qc.invalidateQueries({ queryKey: qk.events.templates(eventId) });
    },
    onError: (err) => {
      toastApiError(err, 'Template sync failed');
    },
  });
}
